<template>
    <div>
        <div class="top-header">
            <h1>Turmål</h1>
        </div>
        <div class="page__content page__content--toplist">
            <form @submit.prevent id="toplist-filter">
                <div class="select select--competitions select--municipality" v-if="filters.municipalities.length">
                    <select v-model="municipality">
                        <option value="">Velg kommune</option>
                        <option v-for="m in filters.municipalities" :key="m.id" :value="m.id">{{ m.txt }}</option>
                    </select>
                </div>
                <div class="select select--competitions">
                    <select v-model="competition">
                        <option v-for="c in filters.competitions" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                </div>
                <input type="text" class="searchfield" v-model="query" placeholder="Søk etter navn">
            </form>

            <div id="toplist-loading" v-if="initialLoading"><div class="toplist__spinner"></div></div>
            <template v-else>
                <div class="toplist-back"><a href="#" class="action" @click.prevent="$emit('back')">&laquo; Tilbake</a></div>
                <div class="toplist__content" :class="{ 'toplist__content--loading': loading }">
                    <div class="toplist__loading-overlay" v-if="loading"><div class="toplist__spinner"></div></div>
                    <div class="table" v-if="toplist.length">
                        <table>
                            <thead>
                                <tr>
                                    <th class="table__header--ranking">#</th>
                                    <th class="table__header--name">Turmål</th>
                                    <th class="table__header--name">Kommune</th>
                                    <th class="table__header--targets"><a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'visits' }" @click.prevent="order = 'visits'">Besøk<i v-if="order === 'visits'" class="pi pi-sort-amount-down"></i></a></th>
                                    <th class="table__header--reg"><a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'persons' }" @click.prevent="order = 'persons'">Personer<i v-if="order === 'persons'" class="pi pi-sort-amount-down"></i></a></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(entry, index) in toplist" :key="entry.page_id"
                                    class="table__entry"
                                    :class="{ 'table__entry--first': index === 0, 'table__entry--last': index === toplist.length - 1 }">
                                    <td class="table__ranking table__num">{{ entry.ranking }}</td>
                                    <td class="table__name"><a :href="'/tur/' + entry.page_id" class="table__secondary-link">{{ entry.name }}</a></td>
                                    <td class="table__name">{{ entry.county }}</td>
                                    <td class="table__targets table__num" :class="{ 'table__num--current': order === 'visits' }">{{ entry.reg_count }}</td>
                                    <td class="table__reg table__num" :class="{ 'table__num--current': order === 'persons' }">{{ entry.v_count }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <ToplistPager :current-page="pagination.current_page" :last-page="pagination.last_page" @change="goToPage" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import ToplistPager from './ToplistPager.vue';

const props = defineProps({ competitionId: { type: Number, default: null } });
defineEmits(['back']);

const params = new URLSearchParams(window.location.search);

const region = ref(params.get('region') || '');
const municipality = ref(params.get('municipality') || '');
const competition = ref(params.get('competition') ? Number(params.get('competition')) : (props.competitionId || ''));
const query = ref(params.get('q') || '');
const order = ref(params.get('order') || 'persons');
const page = ref(params.get('page') ? Number(params.get('page')) : 1);

const loading = ref(true);
const initialLoading = ref(true);
const toplist = ref([]);
const pagination = ref({ current_page: 1, last_page: 1, total: 0, per_page: 25 });
const filters = ref({ municipalities: [], regions: [], competitions: [] });

let requestToken = 0;
let searchDebounce = null;

async function load() {
    const token = ++requestToken;
    loading.value = true;

    const search = new URLSearchParams();
    if (region.value) search.set('region', region.value);
    if (municipality.value) search.set('municipality', municipality.value);
    if (competition.value) search.set('competition', competition.value);
    if (query.value) search.set('q', query.value);
    search.set('order', order.value);
    search.set('page', page.value);
    search.set('type', 'routes');
    history.replaceState({}, '', '/topplister?' + search.toString());

    try {
        const response = await fetch('/xhr/frontend/toplists/competition/routes?' + search.toString(), {
            headers: { Accept: 'application/json' },
        });
        if (response.ok && token === requestToken) {
            const data = await response.json();
            toplist.value = data.toplist?.data || [];
            pagination.value = {
                current_page: data.toplist?.current_page || 1,
                last_page: data.toplist?.last_page || 1,
                total: data.toplist?.total || 0,
                per_page: data.toplist?.per_page || 25,
            };
            filters.value = {
                municipalities: data.municipalities || [],
                regions: data.regions || [],
                competitions: data.competitions || [],
            };
        }
    } catch (e) {
        // Leave the previous state in place.
    } finally {
        if (token === requestToken) {
            loading.value = false;
            initialLoading.value = false;
        }
    }
}

function goToPage(next) {
    page.value = next;
}

// Any filter change refetches and resets to page 1, except a direct page change.
watch([region, municipality, competition, order], () => {
    page.value = 1;
    load();
});

watch(page, load);

watch(query, () => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
        page.value = 1;
        load();
    }, 300);
});

onMounted(load);
</script>
